import { useState } from "react";
import {
  Box,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
} from "@salestrak/ui";

interface IssueTypeCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rejects to keep the dialog open — the caller has already said why. */
  onSubmit: (data: { name: string; description: string }) => Promise<void>;
}

export function IssueTypeCreateDialog({
  open,
  onOpenChange,
  onSubmit,
}: IssueTypeCreateDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState("");

  function handleOpenChange(next: boolean) {
    if (!next) {
      setName("");
      setDescription("");
      setNameError("");
    }
    onOpenChange(next);
  }

  async function handleCreate() {
    if (submitting) return;
    // A blank name says so, rather than a press that does nothing.
    if (!name.trim()) {
      setNameError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), description: description.trim() });
      handleOpenChange(false);
    } catch {
      // Refused (a taken name): the caller toasted the reason, and the typed
      // name stays for the person to fix.
    } finally {
      setSubmitting(false);
    }
  }

  // One Dialog serves both breakpoints, and Enter submits the form.
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup
        dialogOptions={{
          primary: { label: "Create", onClick: handleCreate, loading: submitting },
          cancel: { disabled: submitting },
        }}
      >
        <DialogHeader>
          <DialogTitle>Create Project type</DialogTitle>
          <DialogDescription>
            Add a new Project type to define the structure for your work items.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <Box
            as="form"
            gap="md"
            onSubmit={(e) => {
              e.preventDefault();
              void handleCreate();
            }}
          >
            <Field invalid={!!nameError}>
              <FieldLabel>Name</FieldLabel>
              <Input
                placeholder="Type name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError("");
                }}
                autoFocus
              />
              {nameError ? <FieldError match>{nameError}</FieldError> : null}
            </Field>
            <Field>
              <FieldLabel>Description</FieldLabel>
              <Input
                placeholder="Optional description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Field>
          </Box>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
